import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError, tap, switchMap, filter } from 'rxjs/operators';

import { 
  UnifiedFinancialCalculationService, 
  PropertyFinancialAnalysis 
} from '@core/services/finance/unified-financial-calculation.service';

import { 
  PropertyKPIResults, 
  YearlyCashflowTable 
} from '@core/services/finance/standardized-kpi-calculator.service';

import { 
  UnifiedPropertyFinancials,
  DataQualityResult
} from '@shared/definitions/unified-financial-data.model';

/**
 * PROPERTY FINANCIAL STATE SERVICE
 * 
 * Centralized state management for property financial data, following the proven 
 * InvestmentStateService pattern from the Investment Simulator.
 * 
 * Key responsibilities:
 * - Manage reactive state for property financial calculations
 * - Provide loading and error state management
 * - Cache calculation results for performance
 * - Serve as single point of truth for components
 * - Handle state invalidation when data changes
 * 
 * Pattern: Raw Data → Unified Calculation → Reactive State → Pure Display Components
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyFinancialStateService {

  // Main state observables (following InvestmentStateService pattern)
  private _propertyAnalysis = new BehaviorSubject<PropertyFinancialAnalysis | null>(null);
  private _calculationState = new BehaviorSubject<CalculationState>({
    isCalculating: false,
    error: null,
    lastCalculated: null,
    currentPropertyId: null
  });

  // Public observables for components
  public readonly propertyAnalysis$ = this._propertyAnalysis.asObservable();
  public readonly calculationState$ = this._calculationState.asObservable();

  // Derived observables for common UI needs
  public readonly isCalculating$ = this.calculationState$.pipe(
    map(state => state.isCalculating)
  );

  public readonly hasError$ = this.calculationState$.pipe(
    map(state => !!state.error)
  );

  public readonly errorMessage$ = this.calculationState$.pipe(
    map(state => state.error)
  );

  public readonly hasResults$ = this.propertyAnalysis$.pipe(
    map(results => !!results)
  );

  public readonly currentPropertyId$ = this.calculationState$.pipe(
    map(state => state.currentPropertyId)
  );

  // Data-specific observables for components
  public readonly propertyKPIs$ = this.propertyAnalysis$.pipe(
    map(analysis => analysis?.kpis || null)
  );

  public readonly cashflowTable$ = this.propertyAnalysis$.pipe(
    map(analysis => analysis?.cashflowTable || null)
  );

  public readonly unifiedData$ = this.propertyAnalysis$.pipe(
    map(analysis => analysis?.unifiedData || null)
  );

  public readonly dataQuality$ = this.propertyAnalysis$.pipe(
    map(analysis => analysis?.dataQuality || null)
  );

  constructor(
    private unifiedCalculationService: UnifiedFinancialCalculationService
  ) {
  }

  /**
   * MAIN METHOD: Calculate property financial analysis
   * Follows the same pattern as InvestmentStateService.calculate()
   */
  calculatePropertyFinancials(propertyId: string): Observable<PropertyFinancialAnalysis> {
    if (!propertyId) {
      const error = 'Property ID is required for financial calculation';
      this.setErrorState(error);
      return throwError(() => new Error(error));
    }

    console.log(`[PropertyFinancialStateService] 🧮 Starting calculation for property ${propertyId}`);

    // Update calculation state to loading
    this.setCalculatingState(true, propertyId);
    this.clearErrorState();

    return this.unifiedCalculationService.getPropertyFinancialAnalysis(propertyId).pipe(
      tap(analysis => {
        
        // Update state with successful results
        this._propertyAnalysis.next(analysis);
        this._calculationState.next({
          isCalculating: false,
          error: null,
          lastCalculated: new Date(),
          currentPropertyId: propertyId
        });
      }),
      catchError(error => {
        console.error(`[PropertyFinancialStateService] ❌ Calculation failed for ${propertyId}:`, error);
        
        // Update state with error
        this.setErrorState(error.message || 'Unknown calculation error');
        this.setCalculatingState(false, propertyId);
        
        return throwError(() => error);
      })
    );
  }

  /**
   * Get specific KPI data reactively
   */
  getPropertyKPIs(propertyId: string): Observable<PropertyKPIResults | null> {
    return this.ensureCalculatedForProperty(propertyId).pipe(
      switchMap(() => this.propertyKPIs$)
    );
  }

  /**
   * Get cashflow table reactively
   */
  getCashflowTable(propertyId: string, projectionYears?: number): Observable<YearlyCashflowTable | null> {
    return this.ensureCalculatedForProperty(propertyId).pipe(
      switchMap(() => {
        if (projectionYears) {
          // Get fresh cashflow table with specific projection years
          return this.unifiedCalculationService.getPropertyCashflowTable(propertyId, projectionYears);
        }
        return this.cashflowTable$;
      })
    );
  }

  /**
   * Get unified property data reactively
   */
  getUnifiedData(propertyId: string): Observable<UnifiedPropertyFinancials | null> {
    return this.ensureCalculatedForProperty(propertyId).pipe(
      switchMap(() => this.unifiedData$)
    );
  }

  /**
   * Get data quality information reactively
   */
  getDataQuality(propertyId: string): Observable<DataQualityResult | null> {
    return this.ensureCalculatedForProperty(propertyId).pipe(
      switchMap(() => this.dataQuality$)
    );
  }

  /**
   * Invalidate cached data and trigger recalculation
   */
  invalidateAndRecalculate(propertyId: string): Observable<PropertyFinancialAnalysis> {
    console.log(`[PropertyFinancialStateService] 🔄 Invalidating cache and recalculating for ${propertyId}`);
    
    // Clear cache in unified calculation service
    this.unifiedCalculationService.invalidatePropertyCache(propertyId);
    
    // Clear current state
    this.clearCurrentResults();
    
    // Trigger new calculation
    return this.calculatePropertyFinancials(propertyId);
  }

  /**
   * Clear all state (useful for component cleanup or switching properties)
   */
  clearCurrentResults(): void {
    console.log('[PropertyFinancialStateService] 🧹 Clearing all current results');
    
    this._propertyAnalysis.next(null);
    this._calculationState.next({
      isCalculating: false,
      error: null,
      lastCalculated: null,
      currentPropertyId: null
    });
  }

  /**
   * Get current results synchronously (useful for guards or synchronous checks)
   */
  getCurrentAnalysis(): PropertyFinancialAnalysis | null {
    return this._propertyAnalysis.value;
  }

  /**
   * Get current calculation state synchronously
   */
  getCurrentCalculationState(): CalculationState {
    return this._calculationState.value;
  }

  /**
   * Check if we have results for a specific property
   */
  hasResultsForProperty(propertyId: string): boolean {
    const currentAnalysis = this.getCurrentAnalysis();
    return currentAnalysis?.propertyId === propertyId;
  }

  /**
   * Check if we're currently calculating for a specific property
   */
  isCalculatingForProperty(propertyId: string): boolean {
    const currentState = this.getCurrentCalculationState();
    return currentState.isCalculating && currentState.currentPropertyId === propertyId;
  }

  // PRIVATE HELPER METHODS

  /**
   * Ensure we have calculations for the specified property
   */
  private ensureCalculatedForProperty(propertyId: string): Observable<PropertyFinancialAnalysis> {
    const currentAnalysis = this.getCurrentAnalysis();
    
    // If we already have results for this property, return them
    if (currentAnalysis?.propertyId === propertyId) {
      return this.propertyAnalysis$.pipe(
        filter((analysis): analysis is PropertyFinancialAnalysis => !!analysis)
      );
    }
    
    // Otherwise, trigger calculation
    return this.calculatePropertyFinancials(propertyId);
  }

  /**
   * Set calculating state
   */
  private setCalculatingState(isCalculating: boolean, propertyId: string | null = null): void {
    const currentState = this.getCurrentCalculationState();
    this._calculationState.next({
      ...currentState,
      isCalculating,
      currentPropertyId: propertyId || currentState.currentPropertyId
    });
  }

  /**
   * Set error state
   */
  private setErrorState(error: string): void {
    const currentState = this.getCurrentCalculationState();
    this._calculationState.next({
      ...currentState,
      isCalculating: false,
      error,
      lastCalculated: null
    });
  }

  /**
   * Clear error state
   */
  private clearErrorState(): void {
    const currentState = this.getCurrentCalculationState();
    if (currentState.error) {
      this._calculationState.next({
        ...currentState,
        error: null
      });
    }
  }
}

/**
 * Calculation state interface (extends the investment simulator pattern)
 */
export interface CalculationState {
  isCalculating: boolean;
  error: string | null;
  lastCalculated: Date | null;
  currentPropertyId: string | null;
}

/**
 * Helper interfaces for component data access
 */
export interface PropertyIncomeData {
  grossAnnualIncome: number;
  effectiveGrossIncome: number;
  vacancyLoss: number;
  totalIncome: number;
}

export interface PropertyExpenseData {
  propertyTax: number;
  insurance: number;
  maintenance: number;
  management: number;
  utilities: number;
  other: number;
  total: number;
}

export interface PropertyPerformanceData {
  netOperatingIncome: number;
  grossYield: number;
  netYield: number;
  capRate: number;
  cashOnCashReturn: number;
  returnOnInvestment: number;
} 